/**
 * PUBLIC (guest-first) client for the teacher page (F3-D). Same pattern as
 * `fetchCertificateVerification`: a plain `fetch` against the public REST
 * `/api/public/teachers/:orgSlug/:slug` (no typed client, no session). A
 * 404 = profile doesn't exist or isn't public — nothing else is filtered.
 */

/** Issuing organization (public branding). Mirrors `models::PublicOrg`. */
export interface PublicOrgDTO {
  name: string | null;
  slug: string | null;
  logo: string | null;
  branding: unknown | null;
}

/** Public teacher. Mirrors `models::PublicTeacher`. */
export interface PublicTeacherDTO {
  name: string | null;
  title: string | null;
  bio: string | null;
  /** JSON of specialties (array), or null. */
  specialties: unknown | null;
  photo: string | null;
  publicSlug: string | null;
}

/** Public course card. Mirrors `models::PublicCourseCard`. */
export interface PublicCourseCardDTO {
  id: string;
  slug: string | null;
  title: string | null;
  description: string | null;
  category: string | null;
  coverImage: string | null;
}

/** Response of `GET /api/public/teachers/:orgSlug/:slug`. */
export interface PublicTeacherPageDTO {
  org: PublicOrgDTO;
  teacher: PublicTeacherDTO;
  courses: PublicCourseCardDTO[];
}

/** Normalizes `specialties` (can arrive as a JSON array or a string) to `string[]`. */
export function specialtiesToChips(specialties: unknown): string[] {
  if (Array.isArray(specialties)) {
    return specialties.filter((s): s is string => typeof s === "string" && s.trim().length > 0);
  }
  if (typeof specialties === "string" && specialties.trim().length > 0) {
    return specialties
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Downloads a teacher's public page. Throws (with `.status`) on non-2xx —
 * the consumer maps 404 → "not found" state.
 */
export async function fetchPublicTeacherPage(
  baseUrl: string,
  orgSlug: string,
  slug: string,
  opts: { signal?: AbortSignal } = {},
): Promise<PublicTeacherPageDTO> {
  const url = `${baseUrl.replace(/\/$/, "")}/api/public/teachers/${encodeURIComponent(
    orgSlug,
  )}/${encodeURIComponent(slug)}`;
  const res = await fetch(url, { signal: opts.signal });
  if (!res.ok) {
    const err = new Error(`teacher page fetch failed: ${res.status}`) as Error & {
      status?: number;
    };
    err.status = res.status;
    throw err;
  }
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- trust-boundary parse of our server's typed DTO
  return (await res.json()) as PublicTeacherPageDTO;
}
